import { AlertCircle, ArrowLeft, ArrowRight, Lock } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { AuthShell } from "../components/AuthShell";
import { authService } from "../services/authService";

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { t, activeLanguage } = useTranslations("login");
  const { t: tg } = useTranslations("global");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const schema = useMemo(
    () =>
      z.object({
        email: z.string().min(1, tg("input_error_required")).email(tg("input_error_invalid_email")),
      }),
    [tg],
  );

  const form = useZodForm(schema, { email: "" }, "onChange");
  const {
    formState: { isValid },
  } = form;

  const handleSubmit = async (values: { email: string }) => {
    setError(null);
    setIsLoading(true);
    try {
      await authService.requestPasswordReset(values.email);
      navigate(`/auth/forgot-password/verify?email=${encodeURIComponent(values.email)}`);
    } catch (err) {
      setError(HttpApiService.getErrorMessage(err) || t("fp_error_network"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="flex flex-col items-center w-full max-w-md mx-auto">
        <div className="flex flex-col items-center gap-4 mb-8 animate-fade-down">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
            <div className="relative bg-primary/10 p-2 rounded-full">
              <Lock className="w-8 h-8 text-primary" />
            </div>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">{t("fp_request_title")}</h2>
            <p className="text-sm text-muted-foreground max-w-sm">{t("fp_request_subtitle")}</p>
          </div>
        </div>

        <ColocaForm
          key={activeLanguage}
          form={form}
          onSubmit={handleSubmit}
          className="w-full space-y-4 animate-fade-up"
        >
          <ColocaInputField
            name="email"
            type="email"
            label={t("fp_request_email_label")}
            placeholder={t("fp_request_email_placeholder")}
            description={t("fp_request_hint")}
          />

          {error && (
            <div className="animate-shake">
              <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />
            </div>
          )}

          <ColocaButton
            type="submit"
            variant="primary"
            size="lg"
            loading={isLoading}
            disabled={!isValid || isLoading}
            className="h-12 w-full text-base font-semibold"
            iconTrailing={ArrowRight}
          >
            {t("fp_request_submit")}
          </ColocaButton>

          <div className="text-center pt-4">
            <Link
              to="/auth/login"
              className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t("fp_request_back")}
            </Link>
          </div>
        </ColocaForm>
      </div>
    </AuthShell>
  );
};

export default ForgotPasswordPage;
